'use client';

import { useEffect, useState } from 'react';

import { defaultLocale, LocalesEnum } from '@/constants/locales';
import az from '@/locales/az.json';
import en from '@/locales/en.json';
import ru from '@/locales/ru.json';
import { recoverFromChunkLoadError } from '@/utils/chunk-load-recovery';
import { logError } from '@/utils/log-error';

const ERROR_TEXTS_BY_LOCALE = {
  [LocalesEnum.AZ]: az.Error,
  [LocalesEnum.EN]: en.Error,
  [LocalesEnum.RU]: ru.Error
} as const;

const SUPPORTED_LOCALES = Object.keys(
  ERROR_TEXTS_BY_LOCALE
) as (keyof typeof ERROR_TEXTS_BY_LOCALE)[];

const detectLocaleFromHost = (): LocalesEnum => {
  if (typeof window === 'undefined') return defaultLocale;

  const host = window.location.hostname;

  if (host.startsWith('en.')) return LocalesEnum.EN;
  if (host.startsWith('ru.')) return LocalesEnum.RU;

  return defaultLocale;
};

// Synchronously sets `<html lang>` from the request host BEFORE React hydrates,
// so that the CSS rules below pick the correct localised block during the very
// first paint (even when the page is the result of a server-thrown error and
// `headers()` is not reachable from this Client Component).
const HOST_LOCALE_SCRIPT = `(function(){try{var h=location.hostname;var l=h.indexOf('en.')===0?'en':h.indexOf('ru.')===0?'ru':'az';document.documentElement.lang=l;}catch(e){}})();`;

// Show only the localised block whose `data-locale` matches `<html lang>`.
// All three locales are present in the SSR HTML so swapping the attribute is
// instantaneous and does not require React to re-render.
const HOST_LOCALE_STYLE =
  '[data-locale]{display:none}html[lang="az"] [data-locale="az"],html[lang="en"] [data-locale="en"],html[lang="ru"] [data-locale="ru"]{display:revert}';

export default function GlobalError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Lazy initialiser so React's client tree matches whatever the inline script
  // already wrote into `<html lang>` and we don't fight the DOM on hydration.
  const [locale] = useState<LocalesEnum>(detectLocaleFromHost);

  useEffect(() => {
    // A stale deploy can reference chunks that 404. Recover with a one-time hard
    // reload (which `reset()` cannot do) before falling back to the error page.
    if (recoverFromChunkLoadError(error)) return;

    logError(error, {
      component: 'GlobalError',
      digest: error.digest
    });
  }, [error]);

  return (
    <html lang={locale} suppressHydrationWarning>
      <head>
        <style dangerouslySetInnerHTML={{ __html: HOST_LOCALE_STYLE }} />
        <script dangerouslySetInnerHTML={{ __html: HOST_LOCALE_SCRIPT }} />
      </head>
      <body>
        <h2>
          {SUPPORTED_LOCALES.map(lng => (
            <span key={lng} data-locale={lng}>
              {ERROR_TEXTS_BY_LOCALE[lng].title}
            </span>
          ))}
        </h2>
        <button type="button" onClick={() => reset()}>
          {SUPPORTED_LOCALES.map(lng => (
            <span key={lng} data-locale={lng}>
              {ERROR_TEXTS_BY_LOCALE[lng].button_label}
            </span>
          ))}
        </button>
      </body>
    </html>
  );
}
